import { FC, useContext, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import NotificationDispatch, { showErrorNotification } from "../../../../../context/notificationContext";
import { useSearchContext } from "../../../../../context/searchContext";
import { useDebounce } from "../../../../../hooks/useDebounce";
import { useFilterSearch } from "../../../../../hooks/useFilterSearch";
import useListFilters from "../../../../../hooks/useListFilters";
import useI18n from "../../../../../hooks/useTranslations";
import { AdminItemNormativeExclusion } from "../../../../../http/patrol/types/itemNormativeExclusions";
import { useItemNormativeExclusionsApi } from "../../../../../http/patrol/useItemNormativesExclusion";
import { PaginationQuery } from "../../../../../types";
import FlexContent from "../../../../ui/FlexContent";
import { getDeleteAction, getEditAction } from "../../../../ui/ListItemMenu";
import ModalDialog from "../../../../ui/ModalDialog";
import SearchBar from "../../../../ui/SearchBar";
import Table from "../../../../ui/Table";
import AddExcludedItemButton from "./AddExcludedItemButton";
import ExcludedItemForm from "./ExcludedItemForm";

type Props = {
  // Header actions (search + create) are only published to the shared Header while
  // this tab is the active one, since Tabs keeps every tab's content mounted at once.
  active?: boolean;
};

const ExcludedItems: FC<Props> = ({ active }) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const { normativeId } = useParams<{ normativeId: string }>();
  const filters = useListFilters({ key: "excludedItems" });
  const aborter = useRef(new AbortController());
  const itemExclusionsApi = useItemNormativeExclusionsApi();
  const { setActions, setTitle } = useSearchContext();
  const { searchTerm, handleSearchChange } = useFilterSearch(filters, aborter, "excludedItems");

  const isActiveTab = active ?? true;

  const debouncedValue = useDebounce(searchTerm);

  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const { data, error } = itemExclusionsApi.useAdminExcludedItems(normativeId!, query, aborter.current.signal);

  const [itemToEdit, setItemToEdit] = useState<AdminItemNormativeExclusion>();
  const [itemToDelete, setItemToDelete] = useState<AdminItemNormativeExclusion>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isActiveTab) return;

    setTitle(i18n.translation.common.excludedItems);
    setActions(
      <>
        <SearchBar placeholder={i18n.translation.items.search} value={searchTerm} onChange={handleSearchChange} />
        <AddExcludedItemButton />
      </>,
    );

    return () => {
      setTitle(undefined);
      setActions(null);
    };
  }, [isActiveTab, searchTerm]);

  return (
    <>
      <FlexContent padding>
        <Table<AdminItemNormativeExclusion>
          filters={filters}
          isTall
          head={
            <tr>
              <th align="left">{i18n.translation.common.code}</th>
              <th align="left">{i18n.translation.common.description}</th>
              <th align="left">{i18n.translation.roofSubstructures.single}</th>
              <th></th>
            </tr>
          }
          items={data?.items}
          renderRow={(item) => (
            <>
              <td>
                <Link onClick={(e) => e.stopPropagation()} to={`/admin/items/${item.item.id}`}>
                  {item.item.code}
                </Link>
              </td>
              <td>{item.item.description}</td>
              <td>{item.roofSubstructure?.name}</td>
            </>
          )}
          onRowClick={(item) => setItemToEdit(item)}
          rowActions={(item) => [
            getEditAction(i18n, () => setItemToEdit(item)),
            getDeleteAction(i18n, () => setItemToDelete(item), true),
          ]}
          total={data?.total}
          error={!!error}
        />
      </FlexContent>
      {!!itemToEdit && (
        <ModalDialog onClose={() => setItemToEdit(undefined)} title={i18n.translation.itemNormativeExclusions.edit}>
          {itemToEdit?.normative.id && (
            <ExcludedItemForm
              item={itemToEdit}
              normativeId={itemToEdit.normative.id}
              onClose={() => setItemToEdit(undefined)}
            />
          )}
        </ModalDialog>
      )}
      {!!itemToDelete && (
        <ModalDialog
          onClose={() => setItemToDelete(undefined)}
          title={i18n.translation.items.delete}
          isSubmitting={isSubmitting}
          actions={[
            {
              title: i18n.translation.common.cancel,
              onClick: () => setItemToDelete(undefined),
              type: "secondary",
            },
            {
              title: i18n.translation.common.yes,
              onClick: () => {
                setIsSubmitting(true);
                itemExclusionsApi
                  .deleteAdminExcludedItem(normativeId!, itemToDelete.id)
                  .then(() => setItemToDelete(undefined))
                  .catch((err) => dispatch(showErrorNotification(i18n, err)))
                  .finally(() => setIsSubmitting(false));
              },
            },
          ]}
        >
          {i18n.translation.itemNormativeExclusions.questions.remove}
        </ModalDialog>
      )}
    </>
  );
};

export default ExcludedItems;
